import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building,
  Building2,
  Plus,
  Search,
  User,
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Edit,
  Trash2,
  Loader2,
  Briefcase,
  Home,
  FolderOpen,
  Wallet
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import BankAccounts from "../components/BankAccounts";

const Clients = () => {
  const { token, isManager } = useAuth();
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  
  // Editing/Delete state
  const [editingClient, setEditingClient] = useState(null);
  const [editingOwner, setEditingOwner] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);
  const [deleteOwner, setDeleteOwner] = useState(null);
  const [deleteProperty, setDeleteProperty] = useState(null);
  
  // Selected items for adding
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  
  // Expanded state
  const [expandedClients, setExpandedClients] = useState({});
  const [expandedOwners, setExpandedOwners] = useState({});
  
  // Saving state
  const [saving, setSaving] = useState(false);

  // Form data
  const [clientFormData, setClientFormData] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    notes: ""
  });

  const [ownerFormData, setOwnerFormData] = useState({
    name: "",
    owner_type: "llc",
    email: "",
    phone: "",
    address: ""
  });

  const [propertyFormData, setPropertyFormData] = useState({
    name: "",
    address: "",
    property_type: "residential",
    units: 1,
    has_basement: false,
    description: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`, { headers });
      setClients(res.data);
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [token]);

  // Toggle functions
  const toggleClient = (clientId) => {
    setExpandedClients(prev => ({ ...prev, [clientId]: !prev[clientId] }));
  };

  const toggleOwner = (ownerId) => {
    setExpandedOwners(prev => ({ ...prev, [ownerId]: !prev[ownerId] }));
  };

  // Client handlers
  const openClientDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setClientFormData({
        name: client.name,
        contact_name: client.contact_name || "",
        email: client.email || "",
        phone: client.phone || "",
        address: client.address || "",
        notes: client.notes || ""
      });
    } else {
      setEditingClient(null);
      setClientFormData({ name: "", contact_name: "", email: "", phone: "", address: "", notes: "" });
    }
    setClientDialogOpen(true);
  };

  const saveClient = async (e) => {
    e.preventDefault();
    if (!clientFormData.name) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, clientFormData, { headers });
        toast.success("Client updated");
      } else {
        await axios.post(`${API}/clients`, clientFormData, { headers });
        toast.success("Client created");
      }
      setClientDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async () => {
    try {
      await axios.delete(`${API}/clients/${deleteClient.id}`, { headers });
      toast.success("Client deleted");
      setDeleteClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete client");
    }
  };

  // Owner handlers
  const openOwnerDialog = (client, owner = null) => {
    setSelectedClient(client);
    if (owner) {
      setEditingOwner(owner);
      setOwnerFormData({
        name: owner.name,
        owner_type: owner.owner_type,
        email: owner.email || "",
        phone: owner.phone || "",
        address: owner.address || ""
      });
    } else {
      setEditingOwner(null);
      setOwnerFormData({ name: "", owner_type: "llc", email: "", phone: "", address: "" });
    }
    setOwnerDialogOpen(true);
  };

  const saveOwner = async (e) => {
    e.preventDefault();
    if (!ownerFormData.name) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const data = { ...ownerFormData, client_id: selectedClient.id };
      if (editingOwner) {
        await axios.put(`${API}/owners/${editingOwner.id}`, data, { headers });
        toast.success("Updated successfully");
      } else {
        await axios.post(`${API}/owners`, data, { headers });
        toast.success("Added successfully");
      }
      setOwnerDialogOpen(false);
      setExpandedClients(prev => ({ ...prev, [selectedClient.id]: true }));
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOwner = async () => {
    try {
      await axios.delete(`${API}/owners/${deleteOwner.id}`, { headers });
      toast.success("Deleted successfully");
      setDeleteOwner(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Property handlers
  const openPropertyDialog = (owner) => {
    setSelectedOwner(owner);
    setPropertyFormData({ name: "", address: "", property_type: "residential", units: 1, has_basement: false, description: "" });
    setPropertyDialogOpen(true);
  };

  const saveProperty = async (e) => {
    e.preventDefault();
    if (!propertyFormData.name || !propertyFormData.address) {
      toast.error("Name and address are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/properties`, { ...propertyFormData, owner_id: selectedOwner.id }, { headers });
      toast.success("Property added");
      setPropertyDialogOpen(false);
      setExpandedOwners(prev => ({ ...prev, [selectedOwner.id]: true }));
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add property");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async () => {
    try {
      await axios.delete(`${API}/properties/${deleteProperty.id}`, { headers });
      toast.success("Property deleted");
      setDeleteProperty(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete property");
    }
  };

  // Styling helpers
  const getOwnerTypeStyle = (type) => {
    switch (type) {
      case "llc": return { bg: "bg-purple-100", text: "text-purple-700", icon: <Briefcase className="w-4 h-4" /> };
      case "trust": return { bg: "bg-amber-100", text: "text-amber-700", icon: <Building2 className="w-4 h-4" /> };
      case "personal": return { bg: "bg-green-100", text: "text-green-700", icon: <User className="w-4 h-4" /> };
      default: return { bg: "bg-slate-100", text: "text-slate-700", icon: <FolderOpen className="w-4 h-4" /> };
    }
  };

  // Filter
  const filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.owners || []).some(o => o.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header"><div className="skeleton h-8 w-48" /></div>
        <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="clients-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="text-slate-500 mt-1">Manage clients and their LLCs, Trusts, Personal holdings</p>
        </div>
        {isManager && (
          <Button onClick={() => openClientDialog()} className="bg-green-700 hover:bg-green-800" data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" />Add Client
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Client List */}
      <div className="space-y-4">
        {filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No clients found</p>
            {isManager && (
              <Button onClick={() => openClientDialog()} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" />Add your first client
              </Button>
            )}
          </div>
        ) : (
          filteredClients.map(client => (
            <Card key={client.id} className="overflow-hidden">
              {/* Client Header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 border-l-4 border-blue-500"
                onClick={() => toggleClient(client.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{client.name}</h3>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                      <span>{client.owner_count || 0} LLCs/Trusts</span>
                      <span>•</span>
                      <span>{client.total_properties || 0} Properties</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isManager && (
                    <>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openClientDialog(client); }}>
                        <Edit className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteClient(client); }} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {expandedClients[client.id] ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                </div>
              </div>

              {/* Client Expanded Content */}
              {expandedClients[client.id] && (
                <div className="border-t bg-slate-50 p-4">
                  {/* Client Info */}
                  {(client.email || client.phone) && (
                    <div className="flex gap-4 text-sm text-slate-600 mb-4">
                      {client.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{client.email}</span>}
                      {client.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{client.phone}</span>}
                    </div>
                  )}

                  {/* Add Owner Button */}
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-slate-700">LLCs / Trusts / Personal</h4>
                    {isManager && (
                      <Button size="sm" onClick={() => openOwnerDialog(client)} className="bg-green-700 hover:bg-green-800">
                        <Plus className="w-4 h-4 mr-1" />Add LLC/Trust/Personal
                      </Button>
                    )}
                  </div>

                  {/* Owners List */}
                  {(!client.owners || client.owners.length === 0) ? (
                    <p className="text-sm text-slate-500 italic text-center py-4">No LLCs, Trusts, or Personal holdings yet</p>
                  ) : (
                    <div className="space-y-3">
                      {client.owners.map(owner => {
                        const style = getOwnerTypeStyle(owner.owner_type);
                        return (
                          <div key={owner.id} className="bg-white rounded-lg border">
                            {/* Owner Header */}
                            <div
                              className="p-3 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                              onClick={() => toggleOwner(owner.id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${style.bg}`}>
                                  <span className={style.text}>{style.icon}</span>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{owner.name}</span>
                                    <Badge className={`${style.bg} ${style.text} text-xs`}>{owner.owner_type.toUpperCase()}</Badge>
                                  </div>
                                  <span className="text-xs text-slate-500">{owner.property_count || 0} properties</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {isManager && (
                                  <>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openOwnerDialog(client, owner); }}>
                                      <Edit className="w-3 h-3 text-slate-500" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={(e) => { e.stopPropagation(); setDeleteOwner(owner); }}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </>
                                )}
                                {expandedOwners[owner.id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                              </div>
                            </div>

                            {/* Owner Expanded - Properties & Bank */}
                            {expandedOwners[owner.id] && (
                              <div className="border-t bg-slate-50 p-3">
                                <Tabs defaultValue="properties" className="w-full">
                                  <TabsList className="grid w-full grid-cols-2 mb-4">
                                    <TabsTrigger value="properties">
                                      <Home className="w-4 h-4 mr-2" />Properties
                                    </TabsTrigger>
                                    <TabsTrigger value="bank">
                                      <Wallet className="w-4 h-4 mr-2" />Bank Accounts
                                    </TabsTrigger>
                                  </TabsList>
                                  
                                  <TabsContent value="properties">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-sm font-medium text-slate-600">Properties</span>
                                      {isManager && (
                                        <Button size="sm" variant="outline" onClick={() => openPropertyDialog(owner)}>
                                          <Plus className="w-3 h-3 mr-1" />Add Property
                                        </Button>
                                      )}
                                    </div>
                                    {(!owner.properties || owner.properties.length === 0) ? (
                                      <p className="text-xs text-slate-500 italic text-center py-2">No properties yet</p>
                                    ) : (
                                      <div className="space-y-2">
                                        {owner.properties.map(prop => (
                                          <div key={prop.id} className="flex items-center justify-between p-2 bg-white rounded border">
                                            <Link to={`/properties/${prop.id}`} className="flex items-center gap-2 flex-1">
                                              <Home className="w-4 h-4 text-slate-400" />
                                              <div>
                                                <p className="text-sm font-medium">{prop.name}</p>
                                                <p className="text-xs text-slate-500">{prop.address}</p>
                                              </div>
                                            </Link>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs text-slate-500">{prop.units} units</span>
                                              {isManager && (
                                                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={() => setDeleteProperty(prop)}>
                                                  <Trash2 className="w-3 h-3" />
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </TabsContent>
                                  
                                  <TabsContent value="bank">
                                    <BankAccounts 
                                      ownerId={owner.id} 
                                      ownerName={owner.name}
                                      ownerType={owner.owner_type}
                                    />
                                  </TabsContent>
                                </Tabs>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Client Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingClient ? "Edit Client" : "Add Client"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveClient} className="space-y-4">
            <div className="space-y-2">
              <Label>Client Name *</Label>
              <Input value={clientFormData.name} onChange={(e) => setClientFormData({...clientFormData, name: e.target.value})} placeholder="e.g., John Smith, AV Group" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input value={clientFormData.contact_name} onChange={(e) => setClientFormData({...clientFormData, contact_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={clientFormData.phone} onChange={(e) => setClientFormData({...clientFormData, phone: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={clientFormData.email} onChange={(e) => setClientFormData({...clientFormData, email: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={clientFormData.notes} onChange={(e) => setClientFormData({...clientFormData, notes: e.target.value})} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setClientDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" disabled={saving}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (editingClient ? "Update" : "Create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Owner Dialog */}
      <Dialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOwner ? "Edit" : "Add"} LLC/Trust/Personal</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveOwner} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              Under client: <strong>{selectedClient?.name}</strong>
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={ownerFormData.owner_type} onValueChange={(v) => setOwnerFormData({...ownerFormData, owner_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="trust">Trust</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={ownerFormData.name} onChange={(e) => setOwnerFormData({...ownerFormData, name: e.target.value})} placeholder="e.g., AV Properties LLC" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={ownerFormData.email} onChange={(e) => setOwnerFormData({...ownerFormData, email: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOwnerDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" disabled={saving}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (editingOwner ? "Update" : "Add")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Property Dialog */}
      <Dialog open={propertyDialogOpen} onOpenChange={setPropertyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Property</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProperty} className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              Under: <strong>{selectedOwner?.name}</strong>
              <Badge className="ml-2" variant="outline">{selectedOwner?.owner_type?.toUpperCase()}</Badge>
            </div>
            <div className="space-y-2">
              <Label>Property Name *</Label>
              <Input value={propertyFormData.name} onChange={(e) => setPropertyFormData({...propertyFormData, name: e.target.value})} placeholder="e.g., Maple Street Apartments" />
            </div>
            <div className="space-y-2">
              <Label>Address *</Label>
              <Input value={propertyFormData.address} onChange={(e) => setPropertyFormData({...propertyFormData, address: e.target.value})} placeholder="123 Main St, City, State" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={propertyFormData.property_type} onValueChange={(v) => setPropertyFormData({...propertyFormData, property_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Units</Label>
                <Input type="number" min="1" value={propertyFormData.units} onChange={(e) => setPropertyFormData({...propertyFormData, units: parseInt(e.target.value) || 1})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPropertyDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" disabled={saving}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Add Property"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialogs */}
      <AlertDialog open={!!deleteClient} onOpenChange={() => setDeleteClient(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client?</AlertDialogTitle>
            <AlertDialogDescription>Delete <strong>{deleteClient?.name}</strong>? Remove all LLCs/Trusts first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteClient} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteOwner} onOpenChange={() => setDeleteOwner(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteOwner?.owner_type?.toUpperCase()}?</AlertDialogTitle>
            <AlertDialogDescription>Delete <strong>{deleteOwner?.name}</strong>? Remove all properties first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOwner} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteProperty} onOpenChange={() => setDeleteProperty(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property?</AlertDialogTitle>
            <AlertDialogDescription>Delete <strong>{deleteProperty?.name}</strong>? This will remove all tenants and leases.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteProperty} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clients;
